import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
  Camera,
  ChevronDown,
  Copy,
  Edit,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Package,
  PackageSearch,
  Printer,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierShipment,
  type CarrierShipmentAddress,
  type CarrierShipmentDetail,
  type CarrierShipmentLabel,
  type CarrierShipmentTask,
  type CarrierTask,
  downloadCarrierBatchShipmentLabels,
  downloadCarrierShipmentLabels,
  useCarrierShipment,
  useCarrierShipmentLabels,
  useCarrierShipments,
  useCarrierTask,
  useGenerateCarrierLabels,
} from "@/api/carrier";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ColumnsMenu,
  SelectFilter,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";
import { PageHeader } from "@/components/page-header";
import { StatusBadge, humanizeStatus } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CancelShipmentDialog } from "@/features/cancel-shipment-dialog";
import { formatDeliveryEta } from "@/features/delivery-eta-display";
import { RequestShipmentChangeDialog } from "@/features/request-shipment-change-dialog";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { formatDate, formatRelativeTime } from "@/lib/format";
import { hasHttpStatus } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { useSession } from "@/services/auth0/session";

const COLUMNS = 12;

const RESPONSIVE: Record<string, string> = {
  carrier: "hidden xl:table-cell",
  from: "hidden lg:table-cell",
  pod: "hidden 2xl:table-cell",
  packages: "hidden lg:table-cell",
  created: "hidden 2xl:table-cell",
};

const TOGGLEABLE_COLUMNS = [
  { id: "carrier", label: "Carrier" },
  { id: "from", label: "From" },
  { id: "packages", label: "Packages" },
  { id: "pod", label: "POD" },
  { id: "created", label: "Created" },
];

const STATUS_FILTERS = [
  "PENDING_PAYMENT",
  "LABEL_READY",
  "DROPPED_AT_CARRIER",
  "PICKED_UP",
  "IN_TRANSIT",
  "AT_FACILITY",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "EXCEPTION",
  "ON_HOLD",
  "RETURNED",
  "CANCELLED",
] as const;

function findAddress(
  shipment: CarrierShipment,
  type: CarrierShipmentAddress["type"]
): CarrierShipmentAddress | undefined {
  return shipment.addresses.find((address) => address.type === type);
}

function addressLine(address: CarrierShipmentAddress | undefined): string {
  if (!address) return "Address unavailable";
  const parts = [
    address.addressLine1 ?? address.address?.line1,
    address.addressLine2 ?? address.address?.line2,
    address.addressCity ?? address.address?.city,
    address.addressProvince ?? address.address?.province,
    address.addressPostalCode ?? address.address?.postalCode,
    address.addressCountry ?? address.address?.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Address unavailable";
}

function DeliveryInstructionsPreview({
  notes,
  label = "Delivery instructions",
  className,
}: {
  notes: string | null;
  label?: string;
  className?: string;
}) {
  const preview = notes?.trim().replace(/\s+/g, " ");
  if (!preview) return null;

  return (
    <p
      className={cn("truncate text-xs text-amber-800", className)}
      title={notes ?? undefined}
    >
      {label}: {preview}
    </p>
  );
}

function AddressCell({
  address,
}: {
  address: CarrierShipmentAddress | undefined;
}) {
  if (!address)
    return <span className="text-muted-foreground">Address unavailable</span>;
  const name = address.contactName || address.contactCompany;
  const city = [
    address.addressCity ?? address.address?.city,
    address.addressProvince ?? address.address?.province,
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <div className="space-y-0.5 text-sm">
      {name && <div className="font-medium">{name}</div>}
      {address.contactPhone && (
        <div className="text-xs text-muted-foreground">
          {address.contactPhone}
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        {city || addressLine(address)}
      </div>
    </div>
  );
}

function money(
  cents: number | null | undefined,
  currency: string | null | undefined = "CAD"
): string {
  if (cents == null) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency || "CAD",
  }).format(cents / 100);
}

function packagesLabel(shipment: CarrierShipment): string {
  const count = shipment.packages.length;
  if (count === 0) return "-";
  const grams = shipment.packages
    .map((item) => item.weightGrams)
    .filter((weight): weight is number => typeof weight === "number")
    .reduce((sum, weight) => sum + weight, 0);
  return grams > 0 ? `${count} · ${(grams / 1000).toFixed(2)} kg` : `${count}`;
}

function proofSummary(tasks: CarrierShipmentTask[]) {
  const task = tasks.find(
    (candidate) =>
      (candidate.type === "CUSTOMER_DELIVERY" ||
        candidate.type === "CARRIER_DROPOFF") &&
      candidate.status === "COMPLETED" &&
      candidate.proof
  );
  if (!task?.proof) return null;
  return {
    notes: task.proof.notes,
    mediaCount: task.proof.mediaItems.length,
  };
}

function taskTimestamp(task: CarrierShipmentTask): string {
  return formatDate(
    task.completedAt ?? task.startedAt ?? task.receivedAt ?? task.createdAt
  );
}

function operatorName(
  task: CarrierShipmentTask,
  operationalTask?: CarrierTask
): string {
  const person =
    operationalTask?.operator?.carrierUser ??
    operationalTask?.trip?.operator?.carrierUser ??
    task.operator?.carrierUser ??
    task.trip?.operator?.carrierUser;
  return person?.name || person?.email || "-";
}

function TaskTimelineItem({
  task,
  loadOperationalContext,
}: {
  task: CarrierShipmentTask;
  loadOperationalContext: boolean;
}) {
  const operational = useCarrierTask(loadOperationalContext ? task.id : null);
  const operationalTask = operational.data;
  const dispatchCenter =
    operationalTask?.dispatchCenter ??
    operationalTask?.depot ??
    task.dispatchCenter;
  const trip = operationalTask?.trip ?? task.trip;

  return (
    <div className="rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-mono text-xs">
            {task.taskNumber ? `TSK-${task.taskNumber}` : task.id}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            <StatusBadge status={task.type} />
            <StatusBadge status={task.status} />
          </div>
        </div>
        <span className="text-xs text-muted-foreground">
          {taskTimestamp(task)}
        </span>
      </div>
      <dl className="mt-2 grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Description</dt>
        <dd className="break-words">{task.description ?? "-"}</dd>
        <dt className="text-muted-foreground">Dispatch center</dt>
        <dd className="break-words">
          {dispatchCenter
            ? `${dispatchCenter.name}${dispatchCenter.code ? ` (${dispatchCenter.code})` : ""}`
            : operational.isLoading
              ? "Loading…"
              : "-"}
        </dd>
        <dt className="text-muted-foreground">Trip</dt>
        <dd className="break-words">
          {trip
            ? `TRP-${trip.tripNumber} (${humanizeStatus(trip.status)})`
            : operational.isLoading
              ? "Loading…"
              : "-"}
        </dd>
        <dt className="text-muted-foreground">Operator</dt>
        <dd className="break-words">{operatorName(task, operationalTask)}</dd>
        <dt className="text-muted-foreground">Task contact</dt>
        <dd className="break-words">
          {[task.contactName, task.contactPhone, task.contactEmail]
            .filter(Boolean)
            .join(" · ") || "-"}
        </dd>
        <dt className="text-muted-foreground">Received</dt>
        <dd>{formatDate(task.receivedAt)}</dd>
        <dt className="text-muted-foreground">Started</dt>
        <dd>{formatDate(task.startedAt)}</dd>
        <dt className="text-muted-foreground">Completed</dt>
        <dd>{formatDate(task.completedAt)}</dd>
        {task.failureReason && (
          <>
            <dt className="text-muted-foreground">Failure</dt>
            <dd className="break-words">
              {task.failureReason}
              {task.failureReasonCode === null ||
              task.failureReasonCode === undefined
                ? ""
                : ` (${task.failureReasonCode})`}
            </dd>
          </>
        )}
      </dl>
      {operational.error && loadOperationalContext && (
        <p className="mt-2 text-xs text-muted-foreground">
          Live Trip and Dispatch Center context is unavailable.
        </p>
      )}
      {task.proof && (
        <div className="mt-3 rounded-md bg-muted/40 p-2 text-xs">
          <div className="font-medium">Proof</div>
          {task.proof.notes && (
            <p className="mt-1 text-muted-foreground">{task.proof.notes}</p>
          )}
          {task.proof.mediaItems.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {task.proof.mediaItems.map((item) => (
                <Button key={item.id} asChild variant="outline" size="sm-touch">
                  <a href={item.url} target="_blank" rel="noreferrer">
                    {item.type.toLowerCase()}
                  </a>
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskTimeline({
  tasks,
  loadOperationalContext = false,
}: {
  tasks: CarrierShipmentTask[];
  loadOperationalContext?: boolean;
}) {
  if (tasks.length === 0) {
    return <p className="text-sm text-muted-foreground">No tasks attached.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Task timeline ({tasks.length})
      </p>
      {tasks.map((task) => (
        <TaskTimelineItem
          key={task.id}
          task={task}
          loadOperationalContext={loadOperationalContext}
        />
      ))}
    </div>
  );
}

function hasInternalLabelMemoToReview(
  labels: CarrierShipmentLabel[],
  deliveryNotes: string | null | undefined
) {
  return labels.some(
    (label) =>
      label.carrier?.selfRated === true &&
      label.deliveryInstructionsSnapshot !== (deliveryNotes ?? "")
  );
}

function PrintLabelsMenu({ shipment }: { shipment: CarrierShipment }) {
  const { token } = useSession();
  const labelsQuery = useCarrierShipmentLabels(shipment.id);
  const generateLabels = useGenerateCarrierLabels();
  const labels = labelsQuery.data ?? [];
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const needsInternalLabelReview = hasInternalLabelMemoToReview(
    labels,
    shipment.deliveryNotes
  );
  const generateLock = useRef(false);
  // `selfRated` = EZX prices this carrier from its own tables, so there is no
  // provider API to buy a label from and EZX PRINTS it. That — printed vs bought —
  // is exactly what this menu is deciding.
  //
  // Tested explicitly against `true`/`false` rather than truthiness: missing data
  // must not silently land in one of the two real branches. That is precisely how
  // the old `accessType` code broke — the column was deleted, every read became
  // `undefined`, and `!== "OWNED"` quietly classified everything as a carrier label.
  const hasCarrierLabels = labels.some(
    (label) => label.carrier?.selfRated === false
  );
  const hasInternalLabels = labels.some(
    (label) => label.carrier?.selfRated === true
  );
  const isSelfRatedCarrier = shipment.carrier?.selfRated === true;
  const isExternalCarrier = shipment.carrier?.selfRated === false;
  const shouldHaveCarrierLabels = isExternalCarrier;
  const shouldHaveInternalLabels =
    isSelfRatedCarrier ||
    (isExternalCarrier && shipment.pickupBookingId !== null);

  const openLabels = async (
    format: string,
    labelType: "carrier" | "internal"
  ) => {
    try {
      const blob = await downloadCarrierShipmentLabels(token, shipment.id, {
        format,
        labelType,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      toast.error("Failed to open labels.");
    }
  };

  const matchingLabels = (labelType: "carrier" | "internal", format: string) =>
    labels.filter((label) => {
      const isInternal = label.carrier?.selfRated === true;
      return (
        label.labelFormat === format &&
        (labelType === "internal" ? isInternal : !isInternal)
      );
    });

  const canOpenFormat = (
    labelType: "carrier" | "internal",
    format: "PDF" | "PNG" | "ZPL"
  ) => {
    const matches = matchingLabels(labelType, format);
    if (matches.length === 0) return false;
    return format !== "PNG" || matches.length === 1;
  };

  const formatSuffix = (
    labelType: "carrier" | "internal",
    format: "PDF" | "PNG" | "ZPL"
  ) => {
    const matches = matchingLabels(labelType, format);
    if (matches.length === 0) return " (none)";
    if (format === "PNG" && matches.length > 1) return " (single only)";
    return "";
  };

  const generate = async (replaceInternal = false) => {
    if (generateLock.current) return;
    generateLock.current = true;
    try {
      const result = await generateLabels.mutateAsync({
        shipmentId: shipment.id,
        labelType: "internal",
        replaceInternal,
      });
      toast.success(result.message ?? "Labels generated.");
      await labelsQuery.refetch();
    } catch {
      toast.error("Failed to generate labels.");
    } finally {
      generateLock.current = false;
    }
  };

  const internalFormats = {
    PDF: hasFormat(labels, "internal", "PDF"),
  };

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="min-h-11 md:min-h-0">
          <Printer className="mr-2 h-4 w-4" />
          Print Labels
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="min-h-11 md:min-h-0">
              Carrier Labels
              {!hasCarrierLabels && (
                <span className="ml-auto text-xs text-muted-foreground">
                  (none)
                </span>
              )}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {!hasCarrierLabels && !shouldHaveCarrierLabels && (
                <DropdownMenuItem className="min-h-11 md:min-h-0" disabled>
                  No carrier labels
                </DropdownMenuItem>
              )}
              {(["PDF", "PNG", "ZPL"] as const).map((format) => (
                <DropdownMenuItem
                  key={format}
                  className="min-h-11 md:min-h-0"
                  disabled={!canOpenFormat("carrier", format)}
                  onClick={() => {
                    void openLabels(format, "carrier");
                  }}
                >
                  {format === "ZPL"
                    ? `ZPL Format (Thermal)${formatSuffix("carrier", format)}`
                    : `${format} Format${formatSuffix("carrier", format)}`}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="min-h-11 md:min-h-0">
              Internal Labels
              {!hasInternalLabels && (
                <span className="ml-auto text-xs text-muted-foreground">
                  (none)
                </span>
              )}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {shouldHaveInternalLabels && !hasInternalLabels ? (
                <DropdownMenuItem
                  onClick={() => {
                    void generate();
                  }}
                  disabled={generateLabels.isPending}
                  className="min-h-11 text-amber-600 md:min-h-0"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {generateLabels.isPending
                    ? "Generating..."
                    : "Generate labels"}
                </DropdownMenuItem>
              ) : (
                <>
                  {needsInternalLabelReview && (
                    <DropdownMenuItem
                      className="min-h-11 text-amber-700 md:min-h-0"
                      onSelect={(event) => {
                        event.preventDefault();
                        setReplaceDialogOpen(true);
                      }}
                    >
                      Review and replace internal labels
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="min-h-11 md:min-h-0"
                    disabled={!internalFormats.PDF}
                    onClick={() => {
                      void openLabels("PDF", "internal");
                    }}
                  >
                    PDF Format{formatSuffix("internal", "PDF")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <AlertDialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace internal labels?</AlertDialogTitle>
            <AlertDialogDescription>
              Open the existing PDF to review its memo first. Replacing it voids
              the current internal labels and creates new PDFs with the current
              delivery instructions. Previously printed copies must be recalled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void generate(true)}>
              Replace labels
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ShipmentActionsMenu({
  shipment,
  onView,
  onRequestChange,
  onCancel,
  includeView = true,
}: {
  shipment: CarrierShipment;
  onView: () => void;
  onRequestChange: () => void;
  onCancel: () => void;
  includeView?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm-touch"
          className="pointer-events-auto relative z-20"
          aria-label={`Actions for SHP-${shipment.shipmentNumber}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        {includeView && (
          <DropdownMenuItem onSelect={() => setTimeout(onView, 0)}>
            <Eye className="mr-2 h-4 w-4" />
            View details
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onRequestChange}>
          <Edit className="mr-2 h-4 w-4" />
          Request changes
        </DropdownMenuItem>
        {shipment.trackingUrl && (
          <DropdownMenuItem
            onSelect={() => window.open(shipment.trackingUrl ?? "", "_blank")}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Track package
          </DropdownMenuItem>
        )}
        {shipment.status !== "PENDING_PAYMENT" && (
          <PrintLabelsMenu shipment={shipment} />
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onCancel}>
          Cancel shipment
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function hasFormat(
  labels: CarrierShipmentLabel[],
  labelType: "carrier" | "internal",
  format: string
) {
  return labels.some((label) => {
    const isInternal = label.carrier?.selfRated === true;
    return (
      label.labelFormat === format &&
      (labelType === "internal" ? isInternal : !isInternal)
    );
  });
}

function ShipmentAddressDetail({
  label,
  address,
  showInstructions = true,
}: {
  label: string;
  address: CarrierShipmentAddress | undefined;
  showInstructions?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-md bg-muted/30 p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 break-words">{addressLine(address)}</p>
      <div className="mt-2 space-y-0.5 break-words text-xs text-muted-foreground">
        <p>
          {[address?.contactName, address?.contactCompany]
            .filter(Boolean)
            .join(" · ") || "No contact name"}
        </p>
        <p>{address?.contactPhone ?? "No contact phone"}</p>
        <p className="break-all">
          {address?.contactEmail ?? "No contact email"}
        </p>
        {showInstructions && (
          <p className="pt-1 text-foreground">
            {address?.instructions ?? "No instructions"}
          </p>
        )}
      </div>
    </div>
  );
}

function ShipmentPackages({ shipment }: { shipment: CarrierShipment }) {
  return (
    <section className="space-y-2 border-t pt-4">
      <h3 className="text-sm font-semibold">
        Packages ({shipment.packages.length})
      </h3>
      {shipment.packages.length === 0 ? (
        <p className="text-sm text-muted-foreground">No packages.</p>
      ) : (
        <div className="space-y-2">
          {shipment.packages.map((item, index) => (
            <dl
              key={item.id}
              className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-3 gap-y-1 rounded-md border p-3 text-xs"
            >
              <dt className="font-medium">Package</dt>
              <dd className="text-right">{(item.index ?? index) + 1}</dd>
              <dt className="text-muted-foreground">Description</dt>
              <dd className="break-words text-right">
                {item.description ?? "-"}
              </dd>
              <dt className="text-muted-foreground">Tracking</dt>
              <dd className="break-all text-right font-mono">
                {item.individualTrackingNumber ?? "-"}
              </dd>
              <dt className="text-muted-foreground">Weight</dt>
              <dd className="text-right">
                {item.weightGrams == null
                  ? "-"
                  : `${item.weightGrams.toLocaleString("en-CA")} g`}
              </dd>
              <dt className="text-muted-foreground">Dimensions</dt>
              <dd className="text-right">
                {[item.lengthCm, item.widthCm, item.heightCm].every(
                  (value) => value != null
                )
                  ? `${item.lengthCm} × ${item.widthCm} × ${item.heightCm} cm`
                  : "-"}
              </dd>
            </dl>
          ))}
        </div>
      )}
    </section>
  );
}

function ShipmentPricing({ shipment }: { shipment: CarrierShipment }) {
  const pricing = shipment.pricing;
  const currency = pricing?.currency ?? "CAD";
  return (
    <section className="space-y-2 border-t pt-4">
      <h3 className="text-sm font-semibold">Pricing</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Base rate</dt>
        <dd className="text-right">
          {money(pricing?.baseRateCents, currency)}
        </dd>
        <dt className="text-muted-foreground">Subtotal</dt>
        <dd className="text-right">
          {money(pricing?.subtotalCents, currency)}
        </dd>
        <dt className="text-muted-foreground">Tax</dt>
        <dd className="text-right">{money(pricing?.taxCents, currency)}</dd>
        <dt className="font-medium">Total</dt>
        <dd className="text-right font-medium">
          {money(pricing?.totalCents, currency)} {currency}
        </dd>
      </dl>
    </section>
  );
}

function ShipmentLabelFormats({ shipment }: { shipment: CarrierShipment }) {
  const labelsQuery = useCarrierShipmentLabels(shipment.id);
  const labels = labelsQuery.data ?? [];
  const needsInternalLabelReview = hasInternalLabelMemoToReview(
    labels,
    shipment.deliveryNotes
  );
  const formats = (internal: boolean) =>
    Array.from(
      new Set(
        labels
          .filter((label) => (label.carrier?.selfRated === true) === internal)
          .map((label) => label.labelFormat)
      )
    ).join(", ");

  return (
    <section className="space-y-2 border-t pt-4">
      <h3 className="text-sm font-semibold">Label formats</h3>
      {labelsQuery.isLoading ? (
        <p role="status" className="text-xs text-muted-foreground">
          Loading label formats…
        </p>
      ) : labelsQuery.error ? (
        <p className="text-xs text-muted-foreground">
          Label formats are unavailable.
        </p>
      ) : (
        <>
          {needsInternalLabelReview && (
            <p role="alert" className="text-xs text-amber-700">
              Internal label memo is unverified or out of date. Review the PDF
              against the current delivery instructions before handoff; replace
              labels from Print Labels if needed.
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Carrier</dt>
            <dd className="text-right">{formats(false) || "None"}</dd>
            <dt className="text-muted-foreground">Internal</dt>
            <dd className="text-right">{formats(true) || "None"}</dd>
          </dl>
        </>
      )}
    </section>
  );
}

function ShipmentDetailDrawer({
  shipmentId,
  fallback,
  open,
  onOpenChange,
  onRequestChange,
  onCancel,
  onCopyTracking,
}: {
  shipmentId: string | null;
  fallback: CarrierShipment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestChange: (shipment: CarrierShipment) => void;
  onCancel: (shipment: CarrierShipment) => void;
  onCopyTracking: (trackingNumber: string) => void;
}) {
  // Gate on `open` so the query stops refetching once the drawer is dismissed.
  // `fallback` is intentionally retained by the caller (content stays put
  // during the slide-out); the query itself is what we want disabled.
  const detailQuery = useCarrierShipment(open ? shipmentId : null);
  const shipment: CarrierShipment | CarrierShipmentDetail | null =
    detailQuery.data ?? fallback;
  const expandedDetail = shipment as CarrierShipmentDetail | null;
  const dispatchCenter = shipment?.tasks.find(
    (task) => task.dispatchCenter
  )?.dispatchCenter;

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={shipment ? `Shipment SHP-${shipment.shipmentNumber}` : "Shipment"}
      description={
        shipment?.order ? `Order ORD-${shipment.order.orderNumber}` : undefined
      }
      widthClassName="sm:max-w-2xl"
    >
      {open && !shipment && detailQuery.isLoading && (
        <div role="status" aria-label="Loading shipment detail">
          <Skeleton className="h-24" />
        </div>
      )}
      {open && !shipment && detailQuery.error && (
        <div role="alert" className="space-y-3 text-sm">
          <p>
            {hasHttpStatus(detailQuery.error, 403)
              ? "You do not have permission to view this Shipment."
              : "Shipment detail could not be loaded."}
          </p>
          {!hasHttpStatus(detailQuery.error, 403) && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => void detailQuery.refetch()}
            >
              Try again
            </Button>
          )}
        </div>
      )}
      {shipment && (
        <div className="min-w-0 space-y-4 text-sm">
          {detailQuery.isLoading && (
            <div role="status" aria-label="Loading shipment detail">
              <Skeleton className="h-16" />
            </div>
          )}
          {detailQuery.error && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="text-sm text-amber-900">
                {hasHttpStatus(detailQuery.error, 403)
                  ? "You do not have permission to load additional shipment detail. The list data remains available."
                  : "Shipment detail could not be refreshed. The list data remains available."}
              </p>
              {!hasHttpStatus(detailQuery.error, 403) && (
                <Button
                  type="button"
                  variant="outline"
                  size="touch"
                  className="bg-background"
                  onClick={() => void detailQuery.refetch()}
                >
                  Try again
                </Button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {shipment.trackingNumber && (
              <Button
                type="button"
                variant="outline"
                size="touch"
                onClick={() => onCopyTracking(shipment.trackingNumber ?? "")}
              >
                <Copy className="h-4 w-4" />
                Copy tracking
              </Button>
            )}
            <ShipmentActionsMenu
              shipment={shipment}
              includeView={false}
              onView={() => undefined}
              onRequestChange={() => onRequestChange(shipment)}
              onCancel={() => onCancel(shipment)}
            />
          </div>
          <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2">
            <dt className="text-muted-foreground">Status</dt>
            <dd>
              <StatusBadge status={shipment.status} />
            </dd>
            <dt className="text-muted-foreground">Tracking</dt>
            <dd className="break-all font-mono text-xs">
              {shipment.trackingNumber ?? "-"}
            </dd>
            <dt className="text-muted-foreground">Tracking page</dt>
            <dd className="min-w-0 break-all text-xs">
              {shipment.trackingUrl ? (
                <a
                  href={shipment.trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {shipment.trackingUrl}
                </a>
              ) : (
                "-"
              )}
            </dd>
            <dt className="text-muted-foreground">Order</dt>
            <dd className="font-mono text-xs">
              {shipment.order ? `ORD-${shipment.order.orderNumber}` : "-"}
            </dd>
            <dt className="text-muted-foreground">Customer number</dt>
            <dd>{shipment.order?.placerCustomer?.customerNumber ?? "-"}</dd>
            <dt className="text-muted-foreground">Carrier service</dt>
            <dd className="break-words">
              {shipment.carrier?.name ?? "-"}
              {shipment.serviceCode ? ` · ${shipment.serviceCode}` : ""}
            </dd>
            <dt className="text-muted-foreground">Carrier code</dt>
            <dd>
              {expandedDetail?.carrierCode ?? shipment.carrier?.code ?? "-"}
            </dd>
            <dt className="text-muted-foreground">Label source</dt>
            <dd>
              {shipment.carrier?.selfRated === true
                ? "EZX internal labels"
                : shipment.carrier?.selfRated === false
                  ? "Carrier labels"
                  : "Not specified"}
            </dd>
            <dt className="text-muted-foreground">Cost</dt>
            <dd>
              {money(shipment.pricing?.totalCents, shipment.pricing?.currency)}
            </dd>
            <dt className="text-muted-foreground">Created</dt>
            <dd>{formatDate(shipment.createdAt)}</dd>
            <dt className="text-muted-foreground">Updated</dt>
            <dd>{formatDate(expandedDetail?.updatedAt)}</dd>
            <dt className="text-muted-foreground">Ship date</dt>
            <dd>{formatDate(shipment.shipDate)}</dd>
            <dt className="text-muted-foreground">ETA</dt>
            <dd>{formatDeliveryEta(shipment)}</dd>
            <dt className="text-muted-foreground">Delivered</dt>
            <dd>{formatDate(expandedDetail?.actualDelivery)}</dd>
            <dt className="text-muted-foreground">Pickup booking</dt>
            <dd className="break-all">{shipment.pickupBookingId ?? "-"}</dd>
            <dt className="text-muted-foreground">Dispatch center</dt>
            <dd className="break-words">
              {dispatchCenter
                ? `${dispatchCenter.name}${dispatchCenter.code ? ` (${dispatchCenter.code})` : ""}`
                : (expandedDetail?.pickupBooking?.dispatchCenterId ?? "-")}
            </dd>
          </dl>

          {shipment.deliveryNotes?.trim() && (
            <section
              className="rounded-md border bg-muted/30 p-3"
              aria-label="Delivery instructions"
            >
              <h3 className="text-sm font-medium">Delivery instructions</h3>
              <p className="mt-1 whitespace-pre-wrap break-words">
                {shipment.deliveryNotes}
              </p>
            </section>
          )}
          <div className="grid gap-2 border-t pt-4 sm:grid-cols-2">
            <ShipmentAddressDetail
              label="From"
              address={findAddress(shipment, "FROM")}
            />
            <ShipmentAddressDetail
              label="To"
              address={findAddress(shipment, "TO")}
              showInstructions={false}
            />
            {findAddress(shipment, "RETURN") && (
              <ShipmentAddressDetail
                label="Return"
                address={findAddress(shipment, "RETURN")}
              />
            )}
          </div>

          <ShipmentPackages shipment={shipment} />
          <ShipmentPricing shipment={shipment} />
          <ShipmentLabelFormats shipment={shipment} />
          <TaskTimeline tasks={shipment.tasks ?? []} loadOperationalContext />
        </div>
      )}
    </DetailDrawer>
  );
}

function ShipmentMobileCard({
  shipment,
  selecting,
  selected,
  onSelect,
  onOpen,
  onRequestChange,
  onCancel,
  onCopyTracking,
}: {
  shipment: CarrierShipment;
  selecting: boolean;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
  onRequestChange: () => void;
  onCancel: () => void;
  onCopyTracking: (trackingNumber: string) => void;
}) {
  return (
    <article className="relative min-w-0 rounded-md border bg-background p-3">
      <Button
        type="button"
        variant="ghost"
        className="absolute inset-0 z-0 h-full w-full rounded-md p-0 hover:bg-muted/40 focus-visible:ring-inset"
        aria-label={
          selecting
            ? `${selected ? "Unselect" : "Select"} SHP-${shipment.shipmentNumber}`
            : `View shipment SHP-${shipment.shipmentNumber}`
        }
        onClick={selecting ? onSelect : onOpen}
      />
      <div className="pointer-events-none relative z-10 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-mono text-xs font-semibold">
            SHP-{shipment.shipmentNumber}
          </h3>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            {shipment.order ? `ORD-${shipment.order.orderNumber}` : "No order"}
            {shipment.order?.placerCustomer
              ? ` · Customer #${shipment.order.placerCustomer.customerNumber}`
              : ""}
          </p>
        </div>
        <div className="pointer-events-auto relative z-20 flex items-center gap-2">
          {selecting && (
            <Checkbox
              aria-label={`Select SHP-${shipment.shipmentNumber}`}
              checked={selected}
              onClick={(event) => event.stopPropagation()}
              onCheckedChange={onSelect}
            />
          )}
          <StatusBadge status={shipment.status} />
        </div>
      </div>

      <div className="pointer-events-none relative z-10 mt-2 flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1 truncate font-mono text-xs">
          {shipment.trackingNumber ?? "No tracking"}
        </span>
        {shipment.trackingNumber && (
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            className="pointer-events-auto relative z-20"
            aria-label={`Copy tracking ${shipment.trackingNumber}`}
            onClick={(event) => {
              event.stopPropagation();
              onCopyTracking(shipment.trackingNumber ?? "");
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="pointer-events-none relative z-10 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-xs">
        <span className="min-w-0 truncate">
          {addressLine(findAddress(shipment, "FROM"))}
        </span>
        <span aria-hidden="true" className="text-muted-foreground">
          →
        </span>
        <span className="min-w-0 truncate text-right">
          {addressLine(findAddress(shipment, "TO"))}
        </span>
      </div>

      <div className="pointer-events-none relative z-10 mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span>
          {shipment.carrier?.name ?? "No carrier"}
          {shipment.serviceCode ? ` · ${shipment.serviceCode}` : ""}
        </span>
        <span>
          {money(shipment.pricing?.totalCents, shipment.pricing?.currency)}
        </span>
        <span>{shipment.packages.length} packages</span>
        <span>{shipment.tasks.length} tasks</span>
        <span>{formatRelativeTime(shipment.createdAt)}</span>
      </div>
      <DeliveryInstructionsPreview
        notes={findAddress(shipment, "FROM")?.instructions ?? null}
        label="Sender pickup instructions"
        className="pointer-events-none relative z-10 mt-2"
      />
      <DeliveryInstructionsPreview
        notes={shipment.deliveryNotes}
        className="pointer-events-none relative z-10 mt-2"
      />

      {!selecting && (
        <div className="pointer-events-none relative z-10 mt-3 flex items-center gap-2 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            size="touch"
            className="pointer-events-auto relative z-20 min-w-0 flex-1"
            onClick={onOpen}
          >
            View shipment
          </Button>
          <ShipmentActionsMenu
            shipment={shipment}
            includeView={false}
            onView={onOpen}
            onRequestChange={onRequestChange}
            onCancel={onCancel}
          />
        </div>
      )}
    </article>
  );
}

function ShipmentsLoading() {
  return (
    <div role="status" aria-label="Loading shipments" className="space-y-2 p-3">
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
      <Skeleton className="h-32" />
    </div>
  );
}

function ShipmentsMessage({
  kind,
  filtered,
  onRetry,
}: {
  kind: "empty" | "error" | "forbidden";
  filtered?: boolean;
  onRetry?: () => void;
}) {
  const message =
    kind === "forbidden"
      ? "You do not have permission to view carrier shipments."
      : kind === "error"
        ? "Shipments could not be loaded."
        : filtered
          ? "No shipments match the current filters."
          : "No shipments yet.";
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      {kind === "empty" ? (
        <PackageSearch className="h-7 w-7 text-muted-foreground" />
      ) : (
        <TriangleAlert className="h-7 w-7 text-destructive" />
      )}
      <p role={kind === "empty" ? undefined : "alert"} className="text-sm">
        {message}
      </p>
      {kind === "error" && onRetry && (
        <Button type="button" variant="outline" size="touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export default function ShipmentsScreen() {
  const { token } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, search, setPage, setPageSize, setSearch, resetPage } =
    usePagedState({ initialSearch: searchParams.get("q") ?? "" });
  const [status, setStatus] = useState("");
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const selectionKey = JSON.stringify([
    search.trim(),
    status,
    dispatchCenterId,
  ]);
  const [selection, setSelection] = useState({
    key: selectionKey,
    ids: new Set<string>(),
  });
  const selectedIds =
    selection.key === selectionKey ? selection.ids : new Set<string>();
  const [selectingOnMobile, setSelectingOnMobile] = useState(false);
  const [batchLabelType, setBatchLabelType] = useState<"carrier" | "internal">(
    "carrier"
  );
  const [printingBatch, setPrintingBatch] = useState(false);
  const [batchErrors, setBatchErrors] = useState<
    Array<{ shipmentId: string; shipmentNumber: number | null; reason: string }>
  >([]);
  const [batchSummary, setBatchSummary] = useState<{
    shipmentCount: number;
    labelCount: number;
    pageCount: number;
  } | null>(null);
  useEffect(() => {
    if (selection.key === selectionKey) return;
    if (selection.ids.size > 0) {
      toast.info("Shipment selection cleared because the list changed.");
    }
    setSelection({ key: selectionKey, ids: new Set() });
    setBatchErrors([]);
    setBatchSummary(null);
  }, [selection, selectionKey]);
  const previousDispatchCenterId = useRef(dispatchCenterId);
  useEffect(() => {
    if (previousDispatchCenterId.current !== dispatchCenterId) {
      previousDispatchCenterId.current = dispatchCenterId;
      resetPage();
    }
  }, [dispatchCenterId, resetPage]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [changeFor, setChangeFor] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [detail, setDetail] = useState<CarrierShipment | null>(null);
  const [detailOpen, setDetailOpen] = useState(() =>
    searchParams.has("shipmentId")
  );
  const [cancelFor, setCancelFor] = useState<CarrierShipment | null>(null);
  const { isVisible, toggle: toggleColumn } = useColumnVisibility(
    "carrier-web:columns:shipments"
  );
  const colClass = (id: string) =>
    isVisible(id) ? (RESPONSIVE[id] ?? "") : "hidden";
  const detailShipmentId = detail?.id ?? searchParams.get("shipmentId");

  const changeDetailOpen = (open: boolean) => {
    setDetailOpen(open);
    if (!open && searchParams.has("shipmentId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("shipmentId");
      setSearchParams(next, { replace: true });
    }
  };

  const openDetail = (shipment: CarrierShipment) => {
    setDetail(shipment);
    setDetailOpen(true);
  };
  const openChangeRequest = (shipment: CarrierShipment) => {
    changeDetailOpen(false);
    setTimeout(
      () =>
        setChangeFor({
          id: shipment.id,
          label: `SHP-${shipment.shipmentNumber}`,
        }),
      0
    );
  };
  const openCancellation = (shipment: CarrierShipment) => {
    changeDetailOpen(false);
    setTimeout(() => setCancelFor(shipment), 0);
  };

  const { data, isLoading, isFetching, error, refetch } = useCarrierShipments({
    page,
    limit: pageSize,
    search: search.trim(),
    ...(status && { status }),
  });

  const rows = data?.data ?? [];
  const pageIds = rows.map((shipment) => shipment.id);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const selectedOffPage = selectedIds.size - selectedOnPage;
  const updateSelection = (ids: string[], checked: boolean) => {
    setSelection((current) => {
      const next = new Set(current.key === selectionKey ? current.ids : []);
      for (const id of ids) checked ? next.add(id) : next.delete(id);
      return { key: selectionKey, ids: next };
    });
    setBatchErrors([]);
    setBatchSummary(null);
  };
  const printSelected = async () => {
    if (selectedIds.size === 0 || printingBatch) return;
    const preview = window.open("", "_blank");
    if (!preview) {
      toast.error("Allow pop-ups to open the printable labels.");
      return;
    }
    preview.document.title = "Preparing labels";
    preview.document.body.textContent = "Preparing selected labels...";
    setPrintingBatch(true);
    setBatchErrors([]);
    setBatchSummary(null);
    try {
      const result = await downloadCarrierBatchShipmentLabels(
        token,
        [...selectedIds],
        batchLabelType
      );
      const url = URL.createObjectURL(result.blob);
      setBatchSummary({
        shipmentCount: selectedIds.size,
        labelCount: result.labelCount,
        pageCount: result.pageCount,
      });
      preview.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      preview.close();
      const data = (error as { response?: { data?: unknown } }).response?.data;
      if (data instanceof Blob) {
        try {
          const payload = JSON.parse(await data.text()) as {
            error?: string;
            shipments?: Array<{
              shipmentId: string;
              shipmentNumber: number | null;
              reason: string;
            }>;
          };
          setBatchErrors(payload.shipments ?? []);
          toast.error(payload.error ?? "Failed to prepare selected labels.");
          return;
        } catch {
          // Fall through to the generic request error.
        }
      }
      toast.error("Failed to prepare selected labels.");
    } finally {
      setPrintingBatch(false);
    }
  };
  const clearBatchSelection = () => {
    setSelection({ key: selectionKey, ids: new Set() });
    setBatchErrors([]);
    setBatchSummary(null);
  };
  const batchActions = (mobile: boolean) => (
    <>
      <span className="text-sm font-medium">
        {selectedIds.size} {selectedIds.size === 1 ? "shipment" : "shipments"}{" "}
        selected
        {selectedOffPage > 0 ? ` · ${selectedOffPage} on other pages` : ""}
      </span>
      <div className={mobile ? "flex items-center gap-2" : "contents"}>
        <NativeSelect
          aria-label="Label type"
          className={mobile ? "min-w-0 flex-1" : "w-48"}
          density={mobile ? "default" : "compact"}
          value={batchLabelType}
          onChange={(event) =>
            setBatchLabelType(event.target.value as "carrier" | "internal")
          }
        >
          <option value="carrier">Carrier labels</option>
          <option value="internal">TooEZ internal labels</option>
        </NativeSelect>
        <Button
          type="button"
          size={mobile ? "touch" : "sm"}
          disabled={printingBatch || selectedIds.size > 100}
          onClick={() => void printSelected()}
        >
          {printingBatch ? "Preparing..." : "Print selected"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size={mobile ? "touch" : "sm"}
          onClick={clearBatchSelection}
        >
          Clear
        </Button>
      </div>
      {selectedIds.size > 100 && (
        <span className="text-sm text-destructive">
          Print up to 100 shipments at a time.
        </span>
      )}
    </>
  );
  const updating = isFetching;

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const copyTracking = async (trackingNumber: string) => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      toast.success("Tracking number copied.");
    } catch {
      toast.error("Couldn't copy — copy it manually.");
    }
  };

  return (
    <div
      className={cn("carrier-page", selectedIds.size > 0 && "pb-44 md:pb-0")}
    >
      <PageHeader title="Shipments" />

      <Card className="overflow-hidden">
        <div className="border-b p-4">
          <TableToolbar
            className="mb-0"
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              resetPage();
            }}
            searchPlaceholder="Tracking, order, contact, address..."
            searchAriaLabel="Search shipments"
          >
            <div className="hidden sm:block">
              <SelectFilter
                ariaLabel="Filter by status"
                value={status || "ALL"}
                onValueChange={(value) => {
                  resetPage();
                  setStatus(value === "ALL" ? "" : value);
                }}
                options={[
                  { value: "ALL", label: "All statuses" },
                  ...STATUS_FILTERS.map((option) => ({
                    value: option,
                    label: humanizeStatus(option),
                  })),
                ]}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:hidden"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters{status ? " (1)" : ""}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:hidden"
              onClick={() => setSelectingOnMobile((current) => !current)}
            >
              {selectingOnMobile ? "Done selecting" : "Select shipments"}
            </Button>
            <div className="hidden md:block">
              <ColumnsMenu
                columns={TOGGLEABLE_COLUMNS}
                isVisible={isVisible}
                onToggle={toggleColumn}
              />
            </div>
          </TableToolbar>
          {selectedIds.size > 0 && (
            <div
              data-testid="desktop-batch-actions"
              className="mt-3 hidden flex-wrap items-center gap-2 border-t pt-3 md:flex"
            >
              {batchActions(false)}
            </div>
          )}
        </div>

        {batchErrors.length > 0 && (
          <div
            role="alert"
            className="border-b border-amber-300 bg-amber-50 p-3 text-sm"
          >
            <p className="font-semibold">
              No labels were printed. Resolve these shipments and retry:
            </p>
            <ul className="list-disc pl-5">
              {batchErrors.map((item) => (
                <li key={`${item.shipmentId}-${item.reason}`}>
                  {item.shipmentNumber === null
                    ? item.shipmentId
                    : `SHP-${item.shipmentNumber}`}
                  : {item.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {batchSummary && (
          <p
            role="status"
            className="border-b px-3 py-2 text-sm text-muted-foreground"
          >
            {batchSummary.shipmentCount}{" "}
            {batchSummary.shipmentCount === 1 ? "shipment" : "shipments"}{" "}
            selected · {batchSummary.labelCount} labels ready ·{" "}
            {batchSummary.pageCount} PDF pages
          </p>
        )}

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent
            side="right"
            className="flex w-full flex-col p-0 sm:max-w-sm"
          >
            <SheetHeader className="border-b px-4 py-4 pr-12 text-left">
              <SheetTitle>Shipment filters</SheetTitle>
              <SheetDescription>
                Narrow the carrier-visible shipment list.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 space-y-1 overflow-y-auto p-4">
              <p className="text-sm font-medium">Shipment status</p>
              <NativeSelect
                aria-label="Filter by status"
                value={status || "ALL"}
                onChange={(event) => {
                  const value = event.target.value;
                  resetPage();
                  setStatus(value === "ALL" ? "" : value);
                }}
                className="w-full"
              >
                <option value="ALL">All statuses</option>
                {STATUS_FILTERS.map((option) => (
                  <option key={option} value={option}>
                    {humanizeStatus(option)}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="grid grid-cols-2 gap-2 border-t p-4">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={!status}
                onClick={() => {
                  setStatus("");
                  resetPage();
                }}
              >
                Clear filters
              </Button>
              <Button
                type="button"
                size="touch"
                onClick={() => setFiltersOpen(false)}
              >
                Show shipments
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {updating && (
          <p
            role="status"
            className="border-b p-3 text-sm text-muted-foreground"
          >
            Updating shipments...
          </p>
        )}

        {error && rows.length > 0 && (
          <div
            role="alert"
            className="flex flex-col gap-2 border-b border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              Shipment refresh failed. Loaded results remain available.
            </span>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="bg-background"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </div>
        )}

        {(isLoading || updating) && rows.length === 0 ? (
          <ShipmentsLoading />
        ) : error && rows.length === 0 ? (
          <ShipmentsMessage
            kind={hasHttpStatus(error, 403) ? "forbidden" : "error"}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <ShipmentsMessage
            kind="empty"
            filtered={Boolean(search.trim() || status)}
          />
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((shipment) => (
                <ShipmentMobileCard
                  key={shipment.id}
                  shipment={shipment}
                  selecting={selectingOnMobile}
                  selected={selectedIds.has(shipment.id)}
                  onSelect={() =>
                    updateSelection(
                      [shipment.id],
                      !selectedIds.has(shipment.id)
                    )
                  }
                  onOpen={() => openDetail(shipment)}
                  onRequestChange={() => openChangeRequest(shipment)}
                  onCancel={() => openCancellation(shipment)}
                  onCopyTracking={(trackingNumber) =>
                    void copyTracking(trackingNumber)
                  }
                />
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label="Select shipments on this page"
                        checked={
                          selectedOnPage === 0
                            ? false
                            : selectedOnPage === pageIds.length
                              ? true
                              : "indeterminate"
                        }
                        disabled={pageIds.length === 0}
                        onCheckedChange={(checked) =>
                          updateSelection(pageIds, checked === true)
                        }
                      />
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      Shipment
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      Tracking
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("carrier"))}
                    >
                      Carrier
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("from"))}
                    >
                      From
                    </TableHead>
                    <TableHead className="whitespace-nowrap">To</TableHead>
                    <TableHead
                      className={cn(
                        "whitespace-nowrap text-center",
                        colClass("pod")
                      )}
                    >
                      POD
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("packages"))}
                    >
                      Packages
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Cost</TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("created"))}
                    >
                      Created
                    </TableHead>
                    <TableHead className="w-12" aria-label="Actions" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((shipment) => {
                    const pod = proofSummary(shipment.tasks ?? []);
                    return (
                      <Fragment key={shipment.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/50"
                          tabIndex={0}
                          aria-label={`Toggle task timeline for SHP-${shipment.shipmentNumber}`}
                          aria-expanded={expanded.has(shipment.id)}
                          onClick={() => toggle(shipment.id)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggle(shipment.id);
                            }
                          }}
                        >
                          <TableCell
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              aria-label={`Select SHP-${shipment.shipmentNumber}`}
                              checked={selectedIds.has(shipment.id)}
                              onCheckedChange={(checked) =>
                                updateSelection([shipment.id], checked === true)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ChevronDown
                                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                                  expanded.has(shipment.id) ? "rotate-180" : ""
                                }`}
                              />
                              <div>
                                <div className="font-mono text-xs">
                                  SHP-{shipment.shipmentNumber}
                                </div>
                                {shipment.order && (
                                  <div className="font-mono text-xs text-muted-foreground">
                                    ORD-{shipment.order.orderNumber}
                                  </div>
                                )}
                                <DeliveryInstructionsPreview
                                  notes={
                                    findAddress(shipment, "FROM")
                                      ?.instructions ?? null
                                  }
                                  label="Sender pickup instructions"
                                  className="max-w-52"
                                />
                                <DeliveryInstructionsPreview
                                  notes={shipment.deliveryNotes}
                                  className="max-w-52"
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {shipment.trackingNumber ? (
                              <div className="flex items-center gap-1">
                                {shipment.trackingUrl ? (
                                  <a
                                    href={shipment.trackingUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block max-w-[11rem] truncate font-mono text-xs text-primary hover:underline"
                                    title={shipment.trackingNumber}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {shipment.trackingNumber}
                                  </a>
                                ) : (
                                  <span
                                    className="block max-w-[11rem] truncate font-mono text-xs"
                                    title={shipment.trackingNumber}
                                  >
                                    {shipment.trackingNumber}
                                  </span>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  aria-label={`Copy tracking ${shipment.trackingNumber}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void copyTracking(
                                      shipment.trackingNumber ?? ""
                                    );
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1">
                              <StatusBadge status={shipment.status} />
                              {shipment.status === "EXCEPTION" && (
                                <span className="text-xs font-medium text-destructive">
                                  Needs attention
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={colClass("carrier")}>
                            <div className="space-y-1">
                              <Badge variant="outline">
                                {shipment.carrier?.name ??
                                  shipment.carrier?.code ??
                                  "-"}
                              </Badge>
                              <div className="text-xs text-muted-foreground">
                                {shipment.pickupBookingId
                                  ? "Pickup attached"
                                  : "Drop-off"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className={colClass("from")}>
                            <AddressCell
                              address={findAddress(shipment, "FROM")}
                            />
                          </TableCell>
                          <TableCell>
                            <AddressCell
                              address={findAddress(shipment, "TO")}
                            />
                          </TableCell>
                          <TableCell
                            className={cn("text-center", colClass("pod"))}
                          >
                            {pod ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    role="img"
                                    aria-label={`${pod.mediaCount} proof media item${pod.mediaCount === 1 ? "" : "s"}`}
                                    className="inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-xs"
                                  >
                                    <Camera className="h-3.5 w-3.5 text-primary" />
                                    {pod.mediaCount}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="max-w-56">
                                    <p>{pod.mediaCount} proof media item(s)</p>
                                    {pod.notes && (
                                      <p className="mt-1 text-primary-foreground/80">
                                        {pod.notes}
                                      </p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "whitespace-nowrap",
                              colClass("packages")
                            )}
                          >
                            <div className="flex items-center gap-1">
                              <Package className="h-4 w-4 text-muted-foreground" />
                              <span>{packagesLabel(shipment)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {money(
                              shipment.pricing?.totalCents,
                              shipment.pricing?.currency
                            )}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "whitespace-nowrap",
                              colClass("created")
                            )}
                          >
                            <div className="text-sm">
                              <p>{formatRelativeTime(shipment.createdAt)}</p>
                              {shipment.estimatedDelivery && (
                                <p className="text-xs text-muted-foreground">
                                  ETA {formatDeliveryEta(shipment)}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell
                            className="text-right"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <ShipmentActionsMenu
                              shipment={shipment}
                              onView={() => openDetail(shipment)}
                              onRequestChange={() =>
                                openChangeRequest(shipment)
                              }
                              onCancel={() => openCancellation(shipment)}
                            />
                          </TableCell>
                        </TableRow>
                        {expanded.has(shipment.id) && (
                          <TableRow>
                            <TableCell
                              colSpan={COLUMNS}
                              className="bg-muted/30"
                            >
                              <TaskTimeline tasks={shipment.tasks ?? []} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={data.metadata.total}
              totalPages={data.metadata.totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading || updating}
            />
          </div>
        )}
      </Card>

      {selectedIds.size > 0 && (
        <div
          data-testid="mobile-batch-actions"
          className="fixed inset-x-0 bottom-0 z-50 space-y-2 border-t bg-background p-3 shadow-lg md:hidden"
        >
          {batchActions(true)}
        </div>
      )}

      <ShipmentDetailDrawer
        shipmentId={detailShipmentId}
        fallback={detail}
        open={detailOpen}
        onOpenChange={changeDetailOpen}
        onRequestChange={openChangeRequest}
        onCancel={openCancellation}
        onCopyTracking={(trackingNumber) => void copyTracking(trackingNumber)}
      />

      <RequestShipmentChangeDialog
        open={changeFor !== null}
        onOpenChange={(open) => {
          if (!open) setChangeFor(null);
        }}
        shipmentId={changeFor?.id ?? null}
        shipmentLabel={changeFor?.label ?? ""}
      />

      <CancelShipmentDialog
        open={cancelFor !== null}
        onOpenChange={(open) => {
          if (!open) setCancelFor(null);
        }}
        shipment={cancelFor}
      />
    </div>
  );
}
